import type { TechniqueOption, TechniqueFilterValue } from '@features/customization-technique/utils/technique-filter';

import { Zap } from 'lucide-react';

import { cn } from '@shared/utils/cn';
import { ToggleGroup, ToggleGroupItem } from '@shared/components/ui/toggle-group';

import { ALL_TECHNIQUES, getTechniqueDotClass } from '@features/customization-technique/utils/technique-filter';

const COUNT_CLASS =
  'min-w-5 rounded-full bg-muted px-1.5 text-xs leading-5 text-muted-foreground tabular-nums in-data-[state=on]:bg-primary-foreground/20 in-data-[state=on]:text-primary-foreground';

type Props = {
  options: TechniqueOption[];
  value: TechniqueFilterValue;
  onChange: (value: TechniqueFilterValue) => void;
  /** What is counted, singular (« famille », « session »): an « s » is added for the plural. */
  unit: string;
};

/**
 * One click filter by customization technique: there are only a few of them, so they are all visible at once (no
 * menu). The count is the number of items (families, sessions…) of the technique, a lightning bolt flags express orders.
 */
export const TechniqueFilter = ({ options, value, onChange, unit }: Props) => {
  const total = options.reduce((sum, option) => sum + option.count, 0);
  const plural = (count: number) => `${count} ${unit}${count > 1 ? 's' : ''}`;

  return (
    <ToggleGroup
      aria-label="Filtrer par technique de personnalisation"
      className="w-auto flex-wrap"
      // Radix lets a single toggle group be emptied: clicking the active item keeps it selected instead.
      onValueChange={(next) => next && onChange(next === ALL_TECHNIQUES ? ALL_TECHNIQUES : Number(next))}
      type="single"
      value={String(value)}
    >
      <ToggleGroupItem
        aria-label={`Toutes les techniques : ${plural(total)}`}
        className="flex-none cursor-pointer"
        value={ALL_TECHNIQUES}
      >
        Toutes
        <span aria-hidden className={COUNT_CLASS}>
          {total}
        </span>
      </ToggleGroupItem>
      {options.map((option) => (
        <ToggleGroupItem
          aria-label={`${option.name} : ${plural(option.count)}${option.express ? ', dont express' : ''}`}
          className="flex-none cursor-pointer"
          key={option.id}
          value={String(option.id)}
        >
          <span
            aria-hidden
            className={cn('size-2.5 shrink-0 rounded-full ring-1 ring-black/10', getTechniqueDotClass(option.color))}
          />
          {option.name}
          {option.express && (
            <Zap aria-hidden className="size-3.5 text-destructive in-data-[state=on]:text-primary-foreground" />
          )}
          <span aria-hidden className={COUNT_CLASS}>
            {option.count}
          </span>
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};
